
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import CounselorCard from './CounselorCard';
import { CareerCounselor } from '@/hooks/useCareerCounselors';

interface CounselorListingProps {
  counselors: CareerCounselor[];
  loading: boolean;
}

const CounselorListing = ({ counselors, loading }: CounselorListingProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter counselors based on search query
  const filteredCounselors = counselors.filter(counselor => 
    counselor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (counselor.education && counselor.education.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (counselor.about_us && counselor.about_us.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Load the social widget script
  useEffect(() => {
    const loadWidget = () => {
      const script = document.createElement('script');
      script.src = 'https://reviewmanagementtool.com/widget.js';
      script.onload = function() {
        if (window.TestimonialWidget) {
          window.TestimonialWidget.load('afe23437-5a8f-4e52-9be9-8e143a855d03');
        }
      };
      document.head.appendChild(script);
    };

    loadWidget();

    // Cleanup function to remove script when component unmounts
    return () => {
      const existingScript = document.querySelector('script[src="https://reviewmanagementtool.com/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);
  
  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col gap-4 mb-8">
        <h1 className="text-3xl font-bold">Career Counselors</h1>
        
        {/* Search bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search Career Counselors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Social Widget */}
      <div className="mb-8">
        <div id="social-widget-afe23437-5a8f-4e52-9be9-8e143a855d03"></div>
      </div>
      
      {loading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-64 rounded-md bg-muted animate-pulse" />
          ))}
        </div>
      ) : filteredCounselors.length > 0 ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredCounselors.map(counselor => (
            <CounselorCard 
              key={counselor.id} 
              counselor={counselor} 
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-muted-foreground">
            No Career Counselors found
          </h3>
          <p className="mt-2">
            {searchQuery ? 
              "Try adjusting your search query." : 
              "Check back later for available experts."
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default CounselorListing;
